import { useState, useEffect } from 'react'
import axios from 'axios'
import jsPDF from 'jspdf'
import traducciones from '../data/traduccionesLecturas'

const API = process.env.REACT_APP_API_URL

const NIVELES = ['A1','A2','B1','B2','C1','C2']
const COLOR_NIVEL = { A1:'#22C55E', A2:'#3B82F6', B1:'#8B5CF6', B2:'#F59E0B', C1:'#EF4444', C2:'#1B3A8C' }

// Busca la traduccion al ingles de una lectura (clave = titulo en espanol)
function buscarTraduccion(item) {
  if (!item || !item.titulo) return null
  return traducciones[String(item.titulo).trim()] || null
}

// Separa el texto en parrafos (igual que se sembraron)
function separarParrafos(texto) {
  return String(texto || '').trim().split(/\n\s*\n/).map(p => p.trim()).filter(Boolean)
}

// Enlace de respaldo: Google Translate (para lecturas sin traduccion incluida)
function urlGoogleTranslate(texto) {
  const base = 'https://translate.google.com/?sl=es&tl=en&op=translate'
  const codificado = encodeURIComponent(String(texto || ''))
  return codificado.length < 4000 ? base + '&text=' + codificado : base
}

const TX = {
  es: {
    volver: '← Volver a lecturas', cuentoCorto: '📖 Cuento corto', noticia: '📰 Noticia', cuento: '📖 Cuento',
    minLectura: 'min de lectura', min: 'min', pdf: '📥 Descargar en PDF', titulo: 'Lecturas en Español',
    subtitulo: 'Cuentos cortos y noticias para practicar tu comprension lectora.', nivel: 'Nivel', tipo: 'Tipo',
    miNivel: 'Mi nivel', todos: 'Todos', cuentos: '📖 Cuentos', noticias: '📰 Noticias', cargando: 'Cargando...',
    vacio: 'No hay lecturas disponibles para este filtro todavia.', leer: 'Leer ahora →',
    pdfCuento: 'Cuento', pdfNoticia: 'Noticia', pdfNivel: 'Nivel',
    verIngles: '🌐 Ver traducción al inglés', ocultarIngles: '🌐 Ocultar inglés',
    ayudaIngles: '¿Eres principiante? Activa la traducción al inglés debajo de cada párrafo.',
    ingles: 'English', sinTraduccion: 'Esta lectura aún no tiene traducción incluida.',
    traducirGoogle: '🌐 Traducir con Google Translate', copiado: 'Texto copiado: pégalo en el traductor si no aparece.'
  },
  en: {
    volver: '← Back to readings', cuentoCorto: '📖 Short story', noticia: '📰 News', cuento: '📖 Story',
    minLectura: 'min read', min: 'min', pdf: '📥 Download PDF', titulo: 'Readings in Spanish',
    subtitulo: 'Short stories and news to practice your reading comprehension.', nivel: 'Level', tipo: 'Type',
    miNivel: 'My level', todos: 'All', cuentos: '📖 Stories', noticias: '📰 News', cargando: 'Loading...',
    vacio: 'There are no readings for this filter yet.', leer: 'Read now →',
    pdfCuento: 'Story', pdfNoticia: 'News', pdfNivel: 'Level',
    verIngles: '🌐 Show English translation', ocultarIngles: '🌐 Hide English',
    ayudaIngles: "Just starting out? Turn on the English translation under each paragraph.",
    ingles: 'English', sinTraduccion: 'This reading does not have a built-in translation yet.',
    traducirGoogle: '🌐 Translate with Google Translate', copiado: 'Text copied: paste it into the translator if it does not appear.'
  }
}

export default function Lecturas({ usuario, idioma = 'es' }) {
  const t = TX[idioma] || TX.es
  const [nivelUsuario, setNivelUsuario] = useState('A1')
  const [filtroNivel,  setFiltroNivel]  = useState(null) // null = "mi nivel"
  const [filtroTipo,   setFiltroTipo]   = useState(null) // null = todos
  const [lista,        setLista]        = useState([])
  const [cargando,     setCargando]     = useState(true)
  const [seleccionado, setSeleccionado] = useState(null)
  const [cargandoTexto, setCargandoTexto] = useState(false)
  const [verIngles, setVerIngles] = useState(idioma === 'en')
  const [avisoCopia, setAvisoCopia] = useState(false)

  useEffect(() => {
    if (usuario?.id) {
      axios.get(API + '/progreso/estadisticas/' + usuario.id)
        .then(r => setNivelUsuario(r.data.datos?.nivel_actual || 'A1'))
        .catch(() => {})
    }
  }, [usuario])

  useEffect(() => { cargarLista() }, [filtroNivel, filtroTipo, nivelUsuario])

  function cargarLista() {
    setCargando(true)
    const params = {}
    params.nivel = filtroNivel || nivelUsuario
    if (filtroTipo) params.tipo = filtroTipo
    axios.get(API + '/contenido/listar', { params })
      .then(r => setLista(r.data.datos || []))
      .catch(() => setLista([]))
      .finally(() => setCargando(false))
  }

  function abrirLectura(id) {
    setCargandoTexto(true)
    axios.get(API + '/contenido/' + id)
      .then(r => setSeleccionado(r.data.datos))
      .catch(() => {})
      .finally(() => setCargandoTexto(false))
  }

  function copiarTexto(texto) {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(texto).then(() => {
          setAvisoCopia(true)
          setTimeout(() => setAvisoCopia(false), 4000)
        }).catch(() => {})
      }
    } catch (e) { /* sin portapapeles */ }
  }

  function botonIngles() {
    return (
      <button onClick={() => setVerIngles(v => !v)}
        style={{ display:'inline-flex', alignItems:'center', gap:8, background: verIngles ? '#1B3A8C' : '#fff', color: verIngles ? '#fff' : '#1B3A8C',
          border:'1.5px solid #1B3A8C', borderRadius:999, padding:'8px 18px', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer' }}>
        {verIngles ? t.ocultarIngles : t.verIngles}
      </button>
    )
  }

  function descargarPDF(item) {
    const doc = new jsPDF()
    const margen = 18
    const anchoUtil = 210 - margen * 2

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(16)
    doc.text(item.titulo, margen, 22)

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(10)
    doc.setTextColor(120)
    doc.text((item.tipo === 'cuento' ? t.pdfCuento : t.pdfNoticia) + ' \u2014 ' + t.pdfNivel + ' ' + item.nivel + '  \u00b7  EspanolGo by EduNova', margen, 29)

    doc.setDrawColor(220)
    doc.line(margen, 33, 210 - margen, 33)

    doc.setFontSize(12)
    doc.setTextColor(20)
    const lineas = doc.splitTextToSize(item.texto, anchoUtil)

    let y = 43
    const alturaLinea = 6.5
    const alturaPagina = 297 - margen

    lineas.forEach(linea => {
      if (y > alturaPagina) { doc.addPage(); y = 22 }
      doc.text(linea, margen, y)
      y += alturaLinea
    })

    doc.save(item.titulo.replace(/[^a-z0-9]+/gi, '_').toLowerCase() + '.pdf')
  }

  if (seleccionado) {
    const trad = buscarTraduccion(seleccionado)
    const parrafosEs = separarParrafos(seleccionado.texto)
    return (
      <div style={{ maxWidth:720, margin:'0 auto' }}>
        <button onClick={() => setSeleccionado(null)}
          style={{ background:'none', border:'none', cursor:'pointer', fontSize:13, color:'#6B7280', fontFamily:'inherit', marginBottom:20, display:'flex', alignItems:'center', gap:6 }}>
          {t.volver}
        </button>

        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
          <span style={{ background:COLOR_NIVEL[seleccionado.nivel], color:'#fff', fontSize:11, fontWeight:800, padding:'3px 10px', borderRadius:999 }}>
            {seleccionado.nivel}
          </span>
          <span style={{ fontSize:12, color:'#6B7280', fontWeight:600 }}>
            {seleccionado.tipo === 'cuento' ? t.cuentoCorto : t.noticia} · {seleccionado.tiempo_lectura_min} {t.minLectura}
          </span>
        </div>

        <h1 style={{ fontSize:26, fontWeight:800, color:'#1A1A2E', marginBottom: verIngles && trad ? 4 : 20, lineHeight:1.3 }}>{seleccionado.titulo}</h1>
        {verIngles && trad && (
          <div style={{ fontSize:17, fontWeight:600, color:'#3B82F6', marginBottom:20, lineHeight:1.3 }}>{trad.titulo}</div>
        )}

        <div style={{ marginBottom:16 }}>{botonIngles()}</div>

        <div style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:16, padding:32, marginBottom:20 }}>
          {!verIngles ? (
            <p style={{ fontSize:16, lineHeight:1.9, color:'#1F2937', whiteSpace:'pre-wrap' }}>{seleccionado.texto}</p>
          ) : trad && parrafosEs.length === trad.parrafos.length ? (
            parrafosEs.map((p, i) => (
              <div key={i} style={{ marginBottom:20 }}>
                <p style={{ fontSize:16, lineHeight:1.9, color:'#1F2937', margin:0, whiteSpace:'pre-wrap' }}>{p}</p>
                <p style={{ fontSize:14.5, lineHeight:1.75, color:'#1E40AF', margin:'8px 0 0', padding:'8px 12px', background:'#EFF6FF', borderLeft:'3px solid #3B82F6', borderRadius:'0 8px 8px 0' }}>
                  {trad.parrafos[i]}
                </p>
              </div>
            ))
          ) : trad ? (
            <div>
              <p style={{ fontSize:16, lineHeight:1.9, color:'#1F2937', whiteSpace:'pre-wrap' }}>{seleccionado.texto}</p>
              <div style={{ marginTop:20, padding:'12px 14px', background:'#EFF6FF', borderLeft:'3px solid #3B82F6', borderRadius:'0 8px 8px 0' }}>
                <div style={{ fontSize:11, fontWeight:800, color:'#3B82F6', textTransform:'uppercase', letterSpacing:0.5, marginBottom:6 }}>{t.ingles}</div>
                <p style={{ fontSize:14.5, lineHeight:1.75, color:'#1E40AF', margin:0, whiteSpace:'pre-wrap' }}>{trad.parrafos.join('\n\n')}</p>
              </div>
            </div>
          ) : (
            <div>
              <p style={{ fontSize:16, lineHeight:1.9, color:'#1F2937', whiteSpace:'pre-wrap' }}>{seleccionado.texto}</p>
              <div style={{ marginTop:20, padding:'12px 14px', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:10 }}>
                <div style={{ fontSize:13, color:'#92400E', marginBottom:10 }}>{t.sinTraduccion}</div>
                <a href={urlGoogleTranslate(seleccionado.texto)} target="_blank" rel="noopener noreferrer"
                  onClick={() => copiarTexto(seleccionado.texto)}
                  style={{ display:'inline-block', background:'#1B3A8C', color:'#fff', textDecoration:'none', borderRadius:8, padding:'8px 16px', fontSize:13, fontWeight:700 }}>
                  {t.traducirGoogle}
                </a>
                {avisoCopia && <div style={{ fontSize:12, color:'#92400E', marginTop:8 }}>{t.copiado}</div>}
              </div>
            </div>
          )}
        </div>

        <button onClick={() => descargarPDF(seleccionado)}
          style={{ display:'flex', alignItems:'center', gap:8, background:'#1B3A8C', color:'#fff', border:'none', borderRadius:10, padding:'12px 24px', fontFamily:'inherit', fontSize:14, fontWeight:700, cursor:'pointer' }}>
          {t.pdf}
        </button>
      </div>
    )
  }

  return (
    <div style={{ maxWidth:900, margin:'0 auto' }}>
      <div style={{ background:'linear-gradient(135deg,#0D1F52,#1B3A8C)', borderRadius:16, padding:24, marginBottom:24, color:'#fff' }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:36 }}>📖</span>
          <div>
            <h1 style={{ fontSize:22, fontWeight:800, marginBottom:2 }}>{t.titulo}</h1>
            <p style={{ fontSize:13, opacity:.8 }}>{t.subtitulo}</p>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div style={{ display:'flex', gap:20, marginBottom:20, flexWrap:'wrap' }}>
        <div>
          <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', marginBottom:6, textTransform:'uppercase', letterSpacing:0.5 }}>{t.nivel}</div>
          <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
            <button onClick={() => setFiltroNivel(null)}
              style={{ padding:'6px 14px', borderRadius:999, border:'1.5px solid ' + (!filtroNivel ? '#1B3A8C' : '#E2E6F0'),
                background: !filtroNivel ? '#1B3A8C' : '#fff', color: !filtroNivel ? '#fff' : '#374151',
                fontFamily:'inherit', fontSize:12, fontWeight:700, cursor:'pointer' }}>
              {t.miNivel} ({nivelUsuario})
            </button>
            {NIVELES.map(n => (
              <button key={n} onClick={() => setFiltroNivel(n)}
                style={{ padding:'6px 14px', borderRadius:999, border:'1.5px solid ' + (filtroNivel===n ? COLOR_NIVEL[n] : '#E2E6F0'),
                  background: filtroNivel===n ? COLOR_NIVEL[n] : '#fff', color: filtroNivel===n ? '#fff' : '#374151',
                  fontFamily:'inherit', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                {n}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', marginBottom:6, textTransform:'uppercase', letterSpacing:0.5 }}>{t.tipo}</div>
          <div style={{ display:'flex', gap:6 }}>
            {[{ id:null, label:t.todos }, { id:'cuento', label:t.cuentos }, { id:'noticia', label:t.noticias }].map(f => (
              <button key={f.label} onClick={() => setFiltroTipo(f.id)}
                style={{ padding:'6px 14px', borderRadius:999, border:'1.5px solid ' + (filtroTipo===f.id ? '#7C3AED' : '#E2E6F0'),
                  background: filtroTipo===f.id ? '#7C3AED' : '#fff', color: filtroTipo===f.id ? '#fff' : '#374151',
                  fontFamily:'inherit', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        {botonIngles()}
        {!verIngles && <span style={{ fontSize:12, color:'#6B7280' }}>{t.ayudaIngles}</span>}
      </div>

      {cargando || cargandoTexto ? (
        <div style={{ textAlign:'center', padding:60, color:'#6B7280' }}>{t.cargando}</div>
      ) : lista.length === 0 ? (
        <div style={{ textAlign:'center', padding:60, color:'#6B7280' }}>
          <div style={{ fontSize:40, marginBottom:12 }}>📭</div>
          {t.vacio}
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:16 }}>
          {lista.map(item => (
            <div key={item.id} onClick={() => abrirLectura(item.id)}
              style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, padding:20, cursor:'pointer', transition:'all .18s ease' }}
              onMouseOver={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,0.08)' }}
              onMouseOut={e =>  { e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='none' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                <span style={{ background:COLOR_NIVEL[item.nivel], color:'#fff', fontSize:10, fontWeight:800, padding:'2px 9px', borderRadius:999 }}>
                  {item.nivel}
                </span>
                <span style={{ fontSize:11, color:'#6B7280', fontWeight:600 }}>
                  {item.tipo === 'cuento' ? t.cuento : t.noticia} · {item.tiempo_lectura_min} {t.min}
                </span>
              </div>
              <div style={{ fontSize:16, fontWeight:700, color:'#1A1A2E', marginBottom:6 }}>{item.titulo}</div>
              {verIngles && buscarTraduccion(item) && (
                <div style={{ fontSize:14, fontWeight:600, color:'#3B82F6', marginBottom:6 }}>{buscarTraduccion(item).titulo}</div>
              )}
              {item.resumen && (
                <div style={{ fontSize:13, color:'#6B7280', lineHeight:1.6 }}>{item.resumen}</div>
              )}
              {verIngles && buscarTraduccion(item) && (
                <div style={{ fontSize:12.5, color:'#1E40AF', lineHeight:1.55, marginTop:6, padding:'6px 10px', background:'#EFF6FF', borderRadius:8 }}>{buscarTraduccion(item).resumen}</div>
              )}
              <div style={{ marginTop:12, fontSize:12, color:'#1B3A8C', fontWeight:700 }}>{t.leer}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
